import { useApiMutation, useInvalidateQueries } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';

import { UpdateUserDetailRequest } from '../types/userTypes';

export const useUserMutations = (userUuid: string) => {
  const { invalidateQueries } = useInvalidateQueries();

  const updateUserDetailMutation = useApiMutation<
    string,
    Error,
    UpdateUserDetailRequest
  >(
    (userData) =>
      api.patch<string>(`/api/v1/user/detail/${userUuid}`, userData),
    {
      onSuccess: () => {
        invalidateQueries(['user', 'detail', userUuid]);
      },
      onError: (error: any) => {
        alert(
          `Failed to update user details: ${error?.message || 'Unknown error'}`,
        );
      },
    },
  );

  const validateUserData = (
    userData: UpdateUserDetailRequest,
  ): string | null => {
    // Company Name validation (0-100 characters)
    if (userData.companyName.length > 100) {
      return 'Company name must be 100 characters or less';
    }

    // Company Address validation (0-255 characters)
    if (userData.companyAddress.length > 255) {
      return 'Company address must be 255 characters or less';
    }

    // Representative Name validation (0-100 characters)
    if (userData.representativeName.length > 100) {
      return 'Representative name must be 100 characters or less';
    }

    // Phone Number validation (0-20 characters, matches pattern)
    if (userData.phoneNumber.length > 20) {
      return 'Phone number must be 20 characters or less';
    }
    const phonePattern = /^[0-9+()\-\s]{1,20}$/;
    if (userData.phoneNumber && !phonePattern.test(userData.phoneNumber)) {
      return 'Phone number format is invalid. Only numbers, +, (), -, and spaces are allowed';
    }

    // Business Registration Number validation (0-100 characters)
    if (userData.businessRegistrationNumber.length > 100) {
      return 'Business registration number must be 100 characters or less';
    }

    // Business Certificate URL validation (0-2048 characters)
    if (userData.businessCertificateUrl.length > 2048) {
      return 'Business certificate URL must be 2048 characters or less';
    }

    // Postal Code validation (0-50 characters)
    if (userData.postalCode.length > 50) {
      return 'Postal code must be 50 characters or less';
    }

    return null; // No validation errors
  };

  return {
    updateUserDetail: (userData: UpdateUserDetailRequest) => {
      const validationError = validateUserData(userData);
      if (validationError) {
        alert(validationError);
        return;
      }

      updateUserDetailMutation.mutate(userData);
    },
    isUpdatingUserDetail: updateUserDetailMutation.isPending,
    updateUserDetailError: updateUserDetailMutation.error,
  };
};
